import type { IAddress, IPlace } from "@bbj-types/address";
import type {
  IHistoryConfiguration,
  II18nConfiguration,
  IOrganisation,
  IOrganisationFUP,
} from "@bbj-types/organisation";
import type { OfferTabsLabel } from "@bbj-types/organisation/enum";
import type { IRole } from "@bbj-types/role";
import type { ITeam } from "@bbj-types/team";
import type { IFilterView, IFilterViewMe, IOrganisationFeature, IUser, IUserOrganisations } from "@bbj-types/user";
import type { ObjectId, Override } from "@bbj-types/utils";
import type { OfferBoardTabs } from "@candidate/details/views/offerSearch/logic";
import { useCallback } from "react";
import { axiosUserV2 } from "../axios";

export interface IOfferTabsOrder {
  label: OfferTabsLabel;
  value: OfferBoardTabs;
}

export type IUserMeOrganisation = Override<
  Pick<
    IOrganisation,
    | "_id"
    | "beneficiaryConfiguration"
    | "candidateSchema"
    | "candidateReadOnly"
    | "cer"
    | "childrens"
    | "configuration"
    | "isMaster"
    | "name"
    | "parent"
    | "places"
    | "supportEmail"
    | "coachingStepRules"
  >,
  {
    configuration: {
      filterViews?: IFilterView[];
      helpCenterUrl?: string;
      candidateHistory?: IHistoryConfiguration;
      i18n?: II18nConfiguration;
    };
    features: IOrganisationFeature;
    places: IPlace;
    fup?: Pick<IOrganisationFUP, "onlyManual">;
  }
> & {
  teams?: Override<
    ITeam,
    {
      place: IPlace;
    }
  >[];
};

export type IUserMeOrganisations = Override<
  IUserOrganisations,
  {
    organisation: {
      _id: ObjectId;
      name: string;
      isMaster: boolean;
      childrens: {
        _id: ObjectId;
        name: string;
        isMaster: boolean;
        parent: ObjectId;
        active: boolean;
      }[];
      parent?: {
        _id: ObjectId;
        name: string;
        isMaster: boolean;
        active: boolean;
      };
      active: boolean;
    };
    teams?: ObjectId[];
  }
>;

export interface INotificationPreferences {
  enable: boolean;
  email: boolean;
}

export const NotificationPreferenceFeature = {
  CANDIDATE_OUT: "candidateOut",
} as const;

export type NotificationPreferenceFeature =
  (typeof NotificationPreferenceFeature)[keyof typeof NotificationPreferenceFeature];

export type IUserMe = Override<
  IUser,
  {
    organisation: IUserMeOrganisation;
    organisations: IUserMeOrganisations[];
    filterViews: IFilterViewMe[];
    filterViewsOrder?: Record<string, ObjectId[]>;
    roleV2: IRole;
    manager?: Pick<IUser, "_id" | "firstName" | "lastName" | "phone" | "email" | "isDisabled">;
    notificationPreferences?: Record<NotificationPreferenceFeature, INotificationPreferences>;
    oauth2?: {
      google?: {
        isExpired?: boolean;
        me?: { emails?: { value: string; verified: boolean }[]; name?: { familyName?: string; givenName?: string } };
      };
    };
    offerTabsOrder: IOfferTabsOrder[];
    favoriteAddresses?: IAddress[];
  }
>;

export const useGetUserMe = () => {
  const [state, execute, cancel] = axiosUserV2<IUserMe, void, void>(
    {
      url: "/me",
      method: "GET",
      withCredentials: true,
    },
    { manual: true }
  );

  const callback = useCallback(() => {
    return execute();
  }, [execute]);

  return [state, callback, cancel] as const;
};
